/**
 * tema.ts — tema claro/oscuro de consaludenarm-platform (frosted glass en ambos).
 * Copiar a src/lib/tema.ts.
 *
 * 1) En index.html, dentro de <head> y ANTES del CSS, pega SCRIPT_SIN_PARPADEO (inline) para que la
 *    primera pintura ya tenga el tema correcto:
 *      <script>/* contenido de SCRIPT_SIN_PARPADEO *\/</script>
 * 2) En la UI: const { tema, alternar } = useTema();  <button className="selector-tema" onClick={alternar}>…</button>
 *
 * Orden de prioridad: elección del alumno (localStorage 'cs-tema') → preferencia del sistema → oscuro.
 * Opcional: sincronizar la elección con la API (PATCH /me/preferences { tema }) para que siga al alumno.
 */
import { useCallback, useEffect, useState } from 'react';

export type Tema = 'dark' | 'light';
const CLAVE = 'cs-tema';

export const SCRIPT_SIN_PARPADEO =
  "(function(){try{var t=localStorage.getItem('cs-tema');if(t!=='light'&&t!=='dark'){t=matchMedia('(prefers-color-scheme: light)').matches?'light':'dark'}document.documentElement.dataset.theme=t}catch(e){document.documentElement.dataset.theme='dark'}})();";

function leerGuardado(): Tema | null {
  try {
    const t = localStorage.getItem(CLAVE);
    return t === 'light' || t === 'dark' ? t : null;
  } catch {
    return null;
  }
}

function delSistema(): Tema {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function aplicar(tema: Tema) {
  document.documentElement.dataset.theme = tema;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', tema === 'dark' ? '#0B1530' : '#EAF0F8');
}

export function useTema() {
  const [tema, setTema] = useState<Tema>(
    () =>
      (document.documentElement.dataset.theme as Tema | undefined) ??
      leerGuardado() ??
      delSistema(),
  );

  // Si el alumno no ha elegido, sigue los cambios del sistema
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const onCambio = () => {
      if (!leerGuardado()) {
        const t = delSistema();
        aplicar(t);
        setTema(t);
      }
    };
    mq.addEventListener('change', onCambio);
    return () => mq.removeEventListener('change', onCambio);
  }, []);

  const elegir = useCallback((nuevo: Tema) => {
    try {
      localStorage.setItem(CLAVE, nuevo);
    } catch {
      /* modo privado: solo esta sesión */
    }
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const doc = document as Document & { startViewTransition?: (cb: () => void) => unknown };
    if (doc.startViewTransition && !reduce) doc.startViewTransition(() => aplicar(nuevo));
    else aplicar(nuevo);
    setTema(nuevo);
  }, []);

  const alternar = useCallback(() => elegir(tema === 'dark' ? 'light' : 'dark'), [tema, elegir]);
  return { tema, elegir, alternar };
}
